// src/components/ProductImages.jsx
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs } from "swiper";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import ThumbnailStrip from "./Common/ThumbnailStrip";

export default function ProductImages({ width = "600px", zoomImageData = [] }) {
    const prefixUrl = process.env.REACT_APP_IMAGE_URL || "";

    const images = (zoomImageData || []).map((path) => ({
        image: `${prefixUrl}${path}`,
    }));

    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [mainSwiper, setMainSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);

    // Zoom state
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    useEffect(() => setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0), []);

    // Desktop hover zoom (keeps original behavior)
    const [hoverZoomIndex, setHoverZoomIndex] = useState(null);
    const [hoverZoomOrigin, setHoverZoomOrigin] = useState("50% 50%");

    // Mobile/touch zoom & pan state
    const [zoomedIndex, setZoomedIndex] = useState(null); // index of zoomed slide on mobile
    const [scale, setScale] = useState(1); // 1 or 2 (or configurable)
    const [translate, setTranslate] = useState({ x: 0, y: 0 }); // px offsets for pan
    const lastTouchRef = useRef({ time: 0, x: 0, y: 0, startX: 0, startY: 0, dragging: false });

    // set activeIndex from mainSwiper
    useEffect(() => {
        if (!mainSwiper) return;
        setActiveIndex(mainSwiper.activeIndex || 0);
        const handler = () => setActiveIndex(mainSwiper.activeIndex || 0);
        mainSwiper.on && mainSwiper.on("slideChange", handler);
        return () => mainSwiper.off && mainSwiper.off("slideChange", handler);
    }, [mainSwiper]);

    // ---------- DESKTOP HANDLERS (unchanged) ----------
    const handleMouseMove = (e, idx) => {
        if (isTouchDevice) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setHoverZoomOrigin(`${x}% ${y}%`);
        setHoverZoomIndex(idx);
    };
    const handleMouseEnter = (idx) => {
        if (isTouchDevice) return;
        setHoverZoomIndex(idx);
        setHoverZoomOrigin("50% 50%");
    };
    const handleMouseLeave = () => {
        if (isTouchDevice) return;
        setHoverZoomIndex(null);
        setHoverZoomOrigin("50% 50%");
    };

    // ---------- MOBILE / TOUCH ZOOM & PAN ----------
    const MAX_SCALE = 1.5;
    const MIN_SCALE = 1;

    const resetPan = () => {
        setTranslate({ x: 0, y: 0 });
        lastTouchRef.current = { time: 0, x: 0, y: 0, startX: 0, startY: 0, dragging: false };
    };

    const handleTouchStart = (e, idx) => {
        if (!e.touches || e.touches.length === 0) return;

        const t = e.touches[0];
        const now = Date.now();
        const dt = now - (lastTouchRef.current.time || 0);

        // detect double-tap -> toggle zoom
        if (dt > 0 && dt < 300 && lastTouchRef.current.x && lastTouchRef.current.y) {
            // double tap detected
            if (zoomedIndex === idx && scale > 1) {
                // reset zoom
                setScale(1);
                setZoomedIndex(null);
                resetPan();
            } else {
                // zoom into tapped point
                setZoomedIndex(idx);
                setScale(MAX_SCALE);
                // compute translate such that tapped point moves towards center
                // we'll initialize translate to 0 and let user pan
                setTranslate({ x: 0, y: 0 });
            }
            lastTouchRef.current.time = 0; // consume double-tap
            return;
        }

        // For panning start
        lastTouchRef.current.time = now;
        lastTouchRef.current.x = t.clientX;
        lastTouchRef.current.y = t.clientY;
        lastTouchRef.current.startX = t.clientX;
        lastTouchRef.current.startY = t.clientY;
        lastTouchRef.current.dragging = false;
    };

    const handleTouchMove = (e, idx, wrapperRef) => {
        if (!e.touches || e.touches.length === 0) return;
        if (scale <= 1 || zoomedIndex !== idx) return; // only pan when zoomed
        const t = e.touches[0];

        // start dragging
        const dx = t.clientX - lastTouchRef.current.x;
        const dy = t.clientY - lastTouchRef.current.y;

        // mark dragging true if movement exceeds a small threshold
        if (!lastTouchRef.current.dragging) {
            const totalDx = Math.abs(t.clientX - lastTouchRef.current.startX);
            const totalDy = Math.abs(t.clientY - lastTouchRef.current.startY);
            if (totalDx + totalDy > 6) lastTouchRef.current.dragging = true;
        }

        if (lastTouchRef.current.dragging) {
            e.preventDefault(); // prevent page scroll - needed for smooth pan
            // update translate with boundaries based on wrapper size
            const wrapper = wrapperRef?.current;
            if (!wrapper) {
                setTranslate((p) => ({ x: p.x + dx, y: p.y + dy }));
            } else {
                const rect = wrapper.getBoundingClientRect();
                // when scale = s, the image size is s * rect.width, extra = (s-1)*rect.width
                const s = scale;
                const maxX = Math.max(0, ((s - 1) * rect.width) / 2);
                const maxY = Math.max(0, ((s - 1) * rect.height) / 2);

                setTranslate((prev) => {
                    let nx = prev.x + dx;
                    let ny = prev.y + dy;
                    // clamp
                    nx = Math.max(-maxX, Math.min(maxX, nx));
                    ny = Math.max(-maxY, Math.min(maxY, ny));
                    return { x: nx, y: ny };
                });
            }
            lastTouchRef.current.x = t.clientX;
            lastTouchRef.current.y = t.clientY;
        }
    };

    const handleTouchEnd = (e) => {
        // stop dragging
        lastTouchRef.current.dragging = false;
        // if scale dropped to MIN, reset zoom state
        if (scale <= MIN_SCALE) {
            setZoomedIndex(null);
            resetPan();
        }
    };

    // helper to go to index
    const goToIndex = (idx) => {
        if (mainSwiper && typeof mainSwiper.slideTo === "function") {
            mainSwiper.slideTo(idx);
            setActiveIndex(idx);
            // reset mobile zoom when switching images
            setScale(1);
            setZoomedIndex(null);
            resetPan();
            setHoverZoomIndex(null);
        }
    };

    // reference map for wrapper divs (so touch move can measure bounding box)
    const wrapperRefs = useRef({});
    const setWrapperRef = (idx) => (el) => (wrapperRefs.current[idx] = el);

    return (
        <div style={{ maxWidth: width, margin: "0 auto" }}>
            {/* MAIN SLIDER */}
            <Swiper
                modules={[Navigation, Thumbs]}
                navigation
                onSwiper={setMainSwiper}
                thumbs={{
                    swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
                }}
                spaceBetween={10}
                style={{ "--swiper-navigation-size": "22px" }}
            >
                {images.map((item, idx) => {
                    const isZoomedThis = isTouchDevice ? zoomedIndex === idx && scale > 1 : hoverZoomIndex === idx;
                    const transformStyle = isTouchDevice
                        ? `translate(${translate.x}px, ${translate.y}px) scale(${scale})`
                        : `scale(${isZoomedThis ? 2 : 1})`;

                    const transformOriginStyle = isTouchDevice ? "center center" : (isZoomedThis ? hoverZoomOrigin : "50% 50%");

                    return (
                        <SwiperSlide
                            key={idx}
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <div
                                ref={setWrapperRef(idx)}
                                className="hover-zoom-wrapper"
                                onMouseMove={(e) => handleMouseMove(e, idx)}
                                onMouseEnter={() => handleMouseEnter(idx)}
                                onMouseLeave={handleMouseLeave}
                                onTouchStart={(e) => handleTouchStart(e, idx)}
                                onTouchMove={(e) => handleTouchMove(e, idx, { current: wrapperRefs.current[idx] })}
                                onTouchEnd={handleTouchEnd}
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden", // important for pan bounds
                                    touchAction: scale > 1 ? "none" : "pan-y", // disable browser gestures when zoomed
                                }}
                            >
                                <img
                                    src={item.image}
                                    alt={item.text || `slide-${idx}`}
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                        display: "block",
                                        maxHeight: "80vh",
                                        transition: "transform 180ms ease, transform-origin 0s",
                                        transform: transformStyle,
                                        transformOrigin: transformOriginStyle,
                                        cursor: isTouchDevice ? (scale > 1 ? "grabbing" : "zoom-in") : (isZoomedThis ? "grab" : "zoom-in"),
                                        willChange: "transform",
                                        // disable image dragging OS default
                                        userSelect: "none",
                                        WebkitUserDrag: "none",
                                    }}
                                    loading="lazy"
                                    title={isTouchDevice ? (scale > 1 ? "Drag to pan, double-tap to reset" : "Double-tap to zoom") : "Hover to zoom"}
                                    draggable={false}
                                />
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>

            {/* Thumbnails */}
            <div style={{ marginTop: 12 }}>
                <ThumbnailStrip
                    images={images}
                    isMobile={isTouchDevice}
                    thumbsSwiper={thumbsSwiper}
                    setThumbsSwiper={setThumbsSwiper}
                    onThumbClick={goToIndex}
                    activeIndex={activeIndex}
                />
            </div>

            <style>{`
        .swiper-slide-thumb-active .thumb-wrapper {
          border-color: #fca700 !important;
        }

        .zoom-label {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(0,0,0,0.62);
          color: white;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          z-index: 20;
          pointer-events: none;
          user-select: none;
          text-align: center;
          white-space: nowrap;
        }

        @media (max-width: 480px) {
          .zoom-label { display: none; }
        }
      `}</style>
        </div>
    );
}









// import React, { useEffect, useState } from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Navigation, Thumbs, FreeMode } from "swiper";

// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
// import "swiper/css/thumbs";

// import ThumbnailStrip from "./Common/ThumbnailStrip";

// export default function ProductImages({ width = "600px", zoomImageData = [] }) {
//     const prefixUrl = process.env.REACT_APP_IMAGE_URL || "";

//     const images = (zoomImageData || []).map((path) => ({
//         image: `${prefixUrl}${path}`,
//     }));

//     const [thumbsSwiper, setThumbsSwiper] = useState(null);
//     const [mainSwiper, setMainSwiper] = useState(null);
//     const [activeIndex, setActiveIndex] = useState(0);

//     const [zoomedIndex, setZoomedIndex] = useState(null);
//     const [zoomOrigin, setZoomOrigin] = useState("50% 50%");

//     // responsive flag
//     const [isMobile, setIsMobile] = useState(
//         typeof window !== "undefined" ? window.innerWidth < 480 : false
//     );
//     useEffect(() => {
//         const handleResize = () => setIsMobile(window.innerWidth < 480);
//         window.addEventListener("resize", handleResize);
//         return () => window.removeEventListener("resize", handleResize);
//     }, []);

//     useEffect(() => {
//         if (!mainSwiper) return;
//         // set initial active index
//         setActiveIndex(mainSwiper.activeIndex || 0);
//         const handler = () => setActiveIndex(mainSwiper.activeIndex || 0);
//         // attach listener via Swiper API
//         mainSwiper.on && mainSwiper.on("slideChange", handler);
//         return () => {
//             mainSwiper.off && mainSwiper.off("slideChange", handler);
//         };
//     }, [mainSwiper]);

//     const handleMouseMove = (e, idx) => {
//         const rect = e.currentTarget.getBoundingClientRect();
//         const x = ((e.clientX - rect.left) / rect.width) * 100;
//         const y = ((e.clientY - rect.top) / rect.height) * 100;
//         setZoomOrigin(`${x}% ${y}%`);
//         setZoomedIndex(idx);
//     };

//     const handleMouseEnter = (idx) => {
//         setZoomedIndex(idx);
//         setZoomOrigin("50% 50%");
//     };

//     const handleMouseLeave = () => {
//         setZoomedIndex(null);
//         setZoomOrigin("50% 50%");
//     };

//     const goToIndex = (idx) => {
//         if (mainSwiper && typeof mainSwiper.slideTo === "function") {
//             mainSwiper.slideTo(idx);
//             setActiveIndex(idx);
//         }
//     };

//     return (
//         <div style={{ maxWidth: width, margin: "0 auto" }}>
//             {/* MAIN SLIDER */}
//             <Swiper
//                 modules={[Navigation, Thumbs]}
//                 navigation
//                 onSwiper={setMainSwiper}
//                 thumbs={{
//                     swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
//                 }}
//                 spaceBetween={10}
//                 style={{ "--swiper-navigation-size": "22px" }}
//             >
//                 {images.map((item, idx) => (
//                     <SwiperSlide
//                         key={idx}
//                         style={{
//                             position: "relative",
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                         }}
//                     >
//                         <div
//                             className="hover-zoom-wrapper"
//                             onMouseMove={(e) => handleMouseMove(e, idx)}
//                             onMouseEnter={() => handleMouseEnter(idx)}
//                             onMouseLeave={handleMouseLeave}
//                             style={{
//                                 width: "100%",
//                                 display: "flex",
//                                 justifyContent: "center",
//                                 alignItems: "center",
//                             }}
//                         >
//                             <img
//                                 src={item.image}
//                                 alt={item.text || `slide-${idx}`}
//                                 style={{
//                                     width: "100%",
//                                     height: "auto",
//                                     objectFit: "contain",
//                                     objectPosition: "center",
//                                     display: "block",
//                                     maxHeight: "80vh",
//                                     transition: "transform 220ms ease, transform-origin 0s",
//                                     transform: zoomedIndex === idx ? "scale(2)" : "scale(1)",
//                                     transformOrigin: zoomedIndex === idx ? zoomOrigin : "50% 50%",
//                                     cursor: zoomedIndex === idx ? "grab" : "zoom-in",
//                                 }}
//                                 loading="lazy"
//                                 title="Hover to zoom"
//                                 draggable={false}
//                             />
//                         </div>
//                     </SwiperSlide>
//                 ))}
//             </Swiper>

//             {/* Thumbnails (reusable component) */}
//             <div style={{ marginTop: 12, marginLeft:25 }}>
//                 <ThumbnailStrip
//                     images={images}
//                     isMobile={isMobile}
//                     thumbsSwiper={thumbsSwiper}
//                     setThumbsSwiper={setThumbsSwiper}
//                     onThumbClick={goToIndex}
//                     activeIndex={activeIndex}
//                 />
//             </div>

//             {/* Inline styles */}
//             <style>{`
//         .swiper-slide-thumb-active .thumb-wrapper {
//           border-color: #fca700 !important;
//         }

//         .zoom-label {
//           position: absolute;
//           top: 12px;
//           left: 12px;
//           background: rgba(0,0,0,0.62);
//           color: white;
//           padding: 6px 10px;
//           border-radius: 999px;
//           font-size: 12px;
//           z-index: 20;
//           pointer-events: none;
//           user-select: none;
//           text-align: center;
//           white-space: nowrap;
//         }

//         /* Ensure thumbnail images display completely and without whitespace */
//         .thumb-wrapper img {
//           -o-object-fit: contain;
//           object-fit: contain;
//         }

//         /* mobile thumb row scrollbar */
//         .mobile-thumb-row::-webkit-scrollbar {
//           height: 8px;
//         }
//         .mobile-thumb-row::-webkit-scrollbar-thumb {
//           background: rgba(0,0,0,0.12);
//           border-radius: 99px;
//         }

//         /* Active thumb outline (non-swiper fallback) */
//         .thumb-active-outline {
//           border-color: #fca700 !important;
//         }

//         @media (max-width: 480px) {
//           .zoom-label { display: none; }
//         }
//       `}</style>
//         </div>
//     );
// }








// import React, { useState } from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Navigation, Thumbs } from "swiper";

// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
// import "swiper/css/thumbs";
// import ThumbnailStrip from "./Common/ThumbnailStrip";

// export default function ProductImages({ width = "600px", zoomImageData }) {
//     const prefixUrl = process.env.REACT_APP_IMAGE_URL;

//     const images = zoomImageData?.map((path) => ({
//         image: `${prefixUrl}${path}`,
//     }));

//     const [thumbsSwiper, setThumbsSwiper] = useState(null);
//     const [mainSwiper, setMainSwiper] = useState(null);


//     const [zoomedIndex, setZoomedIndex] = useState(null);
//     const [zoomOrigin, setZoomOrigin] = useState("50% 50%");

//     const handleMouseMove = (e, idx) => {
//         const rect = e.currentTarget.getBoundingClientRect();
//         const x = ((e.clientX - rect.left) / rect.width) * 100;
//         const y = ((e.clientY - rect.top) / rect.height) * 100;
//         setZoomOrigin(`${x}% ${y}%`);
//         setZoomedIndex(idx);
//     };

//     const handleMouseEnter = (idx) => {
//         setZoomedIndex(idx);
//         setZoomOrigin("50% 50%");
//     };

//     const handleMouseLeave = () => {
//         setZoomedIndex(null);
//         setZoomOrigin("50% 50%");
//     };

//     const thumbCount = images?.length || 0;
//     const thumbSize = thumbCount <= 2 ? 160 : 100;
//     const thumbSlidesPerView = thumbCount <= 2 ? thumbCount : Math.min(thumbCount, 5);
//     const thumbSpaceBetween = thumbCount <= 2 ? 16 : 8;
//     const enableCentered = thumbCount <= 2;

//     return (
//         <div style={{ maxWidth: width, margin: "0 auto" }}>
//             {/* MAIN SLIDER */}
//             <Swiper
//                 modules={[Navigation, Thumbs]}
//                 navigation
//                 onSwiper={setMainSwiper}
//                 thumbs={{
//                     swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
//                 }}
//                 spaceBetween={10}
//                 style={{ "--swiper-navigation-size": "22px" }}
//             >
//                 {images?.map((item, idx) => (
//                     <SwiperSlide
//                         key={idx}
//                         style={{
//                             position: "relative",
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                         }}
//                     >
//                         <div
//                             className="hover-zoom-wrapper"
//                             onMouseMove={(e) => handleMouseMove(e, idx)}
//                             onMouseEnter={() => handleMouseEnter(idx)}
//                             onMouseLeave={handleMouseLeave}
//                             style={{
//                                 width: "100%",
//                                 display: "flex",
//                                 justifyContent: "center",
//                                 alignItems: "center",
//                             }}
//                         >
//                             <img
//                                 src={item.image}
//                                 alt={item.text || `slide-${idx}`}
//                                 style={{
//                                     width: "100%",
//                                     height: "auto",
//                                     objectFit: "contain",
//                                     objectPosition: "center",
//                                     display: "block",
//                                     maxHeight: "80vh",
//                                     transition: "transform 220ms ease, transform-origin 0s",
//                                     transform: zoomedIndex === idx ? "scale(2)" : "scale(1)",
//                                     transformOrigin: zoomedIndex === idx ? zoomOrigin : "50% 50%",
//                                     cursor: zoomedIndex === idx ? "grab" : "zoom-in",
//                                 }}
//                                 loading="lazy"
//                                 title="Hover to zoom"
//                                 draggable={false}
//                             />
//                         </div>
//                     </SwiperSlide>
//                 ))}
//             </Swiper>


           

//             <div style={{ marginTop: 12 }}>
//                 {thumbCount <= 2 ? (
//                     <div
//                         style={{
//                             display: "flex",
//                             gap: 16,
//                             justifyContent: "center",
//                             alignItems: "center",
//                             width: "100%",
//                             boxSizing: "border-box",
//                             padding: "8px 0",
//                         }}
//                     >
//                         {images?.map((item, idx) => (
//                             <button
//                                 key={`thumb-flex-${idx}`}
//                                 onClick={() => {
//                                     if (mainSwiper && typeof mainSwiper.slideTo === "function") {
//                                         mainSwiper.slideTo(idx);
//                                     }
//                                 }}
//                                 style={{
//                                     width: thumbSize,
//                                     height: thumbSize,
//                                     padding: 6,
//                                     borderRadius: 8,
//                                     border: "2px solid transparent",
//                                     background: "#fff",
//                                     overflow: "hidden",
//                                     display: "inline-flex",
//                                     alignItems: "center",
//                                     justifyContent: "center",
//                                     boxSizing: "border-box",
//                                     cursor: "pointer",
//                                 }}
//                                 className="thumb-wrapper"
//                                 aria-label={`thumbnail-${idx}`}
//                             >
//                                 <img
//                                     src={item.image}
//                                     alt={item.text || `thumb-${idx}`}
//                                     style={{
//                                         height: "100%",
//                                         width: "auto",
//                                         display: "block",
//                                         objectFit: "contain",
//                                         objectPosition: "center",
//                                         maxWidth: "100%",
//                                         maxHeight: "100%",
//                                     }}
//                                     loading="lazy"
//                                     draggable={false}
//                                 />
//                             </button>
//                         ))}
//                     </div>
//                 ) : (
//                     <div style={{ width: "100%", boxSizing: "border-box", padding: "8px 0" }}>
//                         <Swiper
//                             modules={[Thumbs]}
//                             onSwiper={setThumbsSwiper}
//                             slidesPerView={thumbSlidesPerView}
//                             spaceBetween={thumbSpaceBetween}
//                             watchSlidesProgress
//                             freeMode={true}
//                             centeredSlides={false}
//                             slideToClickedSlide={true}
//                             style={{ padding: 0, overflow: "visible" }}
//                             breakpoints={{
//                                 0: { slidesPerView: Math.min(3, thumbCount) },
//                                 480: { slidesPerView: Math.min(4, thumbCount) },
//                                 768: { slidesPerView: thumbSlidesPerView },
//                             }}
//                         >
//                             {images?.map((item, idx) => (
//                                 <SwiperSlide
//                                     key={`thumb-${idx}`}
//                                     onClick={() => {
//                                         if (mainSwiper && typeof mainSwiper.slideTo === "function") {
//                                             mainSwiper.slideTo(idx);
//                                         }
//                                     }}
//                                     style={{
//                                         cursor: "pointer",
//                                         width: thumbSize,
//                                         height: thumbSize,
//                                         display: "flex",
//                                         alignItems: "center",
//                                         justifyContent: "center",
//                                         boxSizing: "border-box",
//                                         overflow: "visible",
//                                     }}
//                                 >
//                                     <div
//                                         style={{
//                                             border: "2px solid transparent",
//                                             borderRadius: 8,
//                                             overflow: "hidden",
//                                             width: "100%",
//                                             height: "100%",
//                                             display: "flex",
//                                             alignItems: "center",
//                                             justifyContent: "center",
//                                             background: "#fff",
//                                             padding: 6,
//                                             boxSizing: "border-box",
//                                         }}
//                                         className="thumb-wrapper"
//                                     >
//                                         <img
//                                             src={item.image}
//                                             alt={item.text || `thumb-${idx}`}
//                                             style={{
//                                                 height: "100%",
//                                                 width: "auto",
//                                                 display: "block",
//                                                 objectFit: "contain",
//                                                 objectPosition: "center",
//                                                 maxWidth: "100%",
//                                                 maxHeight: "100%",
//                                             }}
//                                             loading="lazy"
//                                             draggable={false}
//                                         />
//                                     </div>
//                                 </SwiperSlide>
//                             ))}
//                         </Swiper>

//                     </div>
//                 )}
//             </div>




//             <style>{`
//         .swiper-slide-thumb-active .thumb-wrapper {
//           border-color: #fca700 !important;
//         }

//         .zoom-label {
//           position: absolute;
//           top: 12px;
//           left: 12px;
//           background: rgba(0,0,0,0.62);
//           color: white;
//           padding: 6px 10px;
//           border-radius: 999px;
//           font-size: 12px;
//           z-index: 20;
//           pointer-events: none;
//           user-select: none;
//           text-align: center;
//           white-space: nowrap;
//         }

//         /* Make sure thumbnail images don't show whitespace for tiny images */
//         .thumb-wrapper img {
//           -o-object-fit: cover;
//           object-fit: cover;
//         }

//         @media (max-width: 768px) {
//           .zoom-label { display: none; }
//         }
//       `}</style>
//         </div>
//     );
// }






